// src/components/Medhistory.jsx
import { useState, useEffect } from 'react';
import Spinner from './Spinner';
import { useUser } from '../contexts/UserContext';    
import toast from 'react-hot-toast';

// --- Data for Checkboxes with Categories ---
const conditionCategories = {
  'Cardiovascular': ['High Blood Pressure', 'High Cholesterol', 'Heart Disease', 'Stroke', 'Phlebitis'],
  'Metabolic/Endocrine': ['Diabetes', 'Thyroid Disease'],
  'Respiratory': ['Asthma', 'COPD', 'Lung Disease - Tuberculosis'],
  'Mental Health': ['Depression', 'Anxiety', 'Alcoholism', 'Drug Abuse', 'Nervous Breakdown - Mental Illness', 'Suicide Attempt'],
  'Gastrointestinal/Liver': ['Liver Disease - Hepatitis - Yellow Jaundice', 'Stomach Ulcer - Duodenum'],
  'Kidney/Bladder': ['Kidney / Bladder Problems'],
  'Blood/Immune': ['Anemia', 'HIV', 'Uncontrolled Bleeding'],
  'Neurological': ['Epilepsy'],
  'Skin': ['Eczema - Hives - Rashes'],
  'Eyes': ['Eye Problems', 'Glaucoma'],
  'Musculoskeletal': ['Arthritis'],
  'Infectious Diseases': ['Mumps - Measles - Chicken Pox', 'Rheumatic Fever', 'Rubella - German Measles', 'Venereal Disease'],
  'Other': ['Cancer', 'Other Illnesses']
};
const allConditions = Object.values(conditionCategories).flat();

// --- Helper Function for Checkbox Handling ---
const handleHistoryChange = (e, fieldName, currentFormData, setFormData) => {
    const { name, checked } = e.target;
    const currentHistory = currentFormData[fieldName] || {};
    const updatedHistory = { ...currentHistory, [name]: checked };
    // Clear 'Other' text if 'Other Illnesses' is unchecked
    if (name === 'Other Illnesses' && !checked) {
        // Assuming the text field name follows a pattern like personalHistoryOtherText
        const otherTextFieldName = `${fieldName}OtherText`;
        setFormData(prev => ({
            ...prev,
            [fieldName]: updatedHistory,
            [otherTextFieldName]: '' // Clear the text field
        }));
    } else {
        setFormData(prev => ({
            ...prev,
            [fieldName]: updatedHistory
        }));
    }
};


// --- Helper to convert API array to state object ---
const mapHistoryArrayToObject = (historyArray = []) => {
    const historyObj = {};
    let otherText = '';
    (historyArray || []).forEach(item => {
        if (typeof item === 'string') {
            if (item.toLowerCase().startsWith('other:')) {
                historyObj['Other Illnesses'] = true;
                otherText = item.substring(6).trim();
            } else if (item === 'Other Illnesses') {
                 historyObj['Other Illnesses'] = true;
            } else {
                // Find the condition key matching case-insensitively
                const conditionKey = allConditions.find(c => c.toLowerCase() === item.toLowerCase()) || item;
                historyObj[conditionKey] = true;
            }
        }
    });
    return { historyObj, otherText };
};

const slugId = (prefix, value) => `${prefix}-${String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;

// --- Component ---
function MedHistory({
    initialData, onSuccess, parentSetLoading, parentSetError,
    onSaveComplete, onSaveSuccess, onDirty, isWorkflowStep = false
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    allergies: '', medications: '', hospitalizations: '',
    personalHistory: {}, personalHistoryOtherText: '',
    familyHistory: {}, familyHistoryOtherText: '',
  });

  const userContext = useUser();
  const user = userContext?.user || {};
  const userid = isWorkflowStep ? initialData?.user_id || initialData?.userid : user?.userid;

  // --- Effect to Initialize/Fetch Data ---
  useEffect(() => {
    let isMounted = true;
    const processData = (data) => {
			if (!data) return;
			// Use the correct API field names for source data
				const personalSource = data.personal_illness || data.personalHistory || [];
				const familySource = data.family_illness || data.familyHistory || [];
				const personalOtherSource = data.personal_illness ? null : data.personalHistoryOtherText;
				const familyOtherSource = data.family_illness ? null : data.familyHistoryOtherText;
				const { historyObj: personalObj, otherText: personalOther } = mapHistoryArrayToObject(personalSource);
				const { historyObj: familyObj, otherText: familyOther } = mapHistoryArrayToObject(familySource);
			setFormData({
				allergies: data.allergies || '',
				medications: data.medications || '',
				hospitalizations: data.hospitalizations || '',
				personalHistory: personalObj,
					personalHistoryOtherText: personalOtherSource ?? personalOther,
					familyHistory: familyObj,
					familyHistoryOtherText: familyOtherSource ?? familyOther,
			});
    };

    if (isWorkflowStep) {
				// Ensure initialData has the expected structure or adjust access
			const dataToProcess = initialData; // Adapt if nested
			processData(dataToProcess);
			setIsLoading(false);
			isMounted = false;
    	return;
		}

		if (!userid) {
				setError("User not identified.");
				setIsLoading(false);
				return;
		}
		setIsLoading(true); setError('');
		try {
			const existingData = userContext?.user;
			if (isMounted) {
				if (existingData && typeof existingData === 'object' && !existingData.error) {
					processData(existingData); // Process fetched data
				} else {
					setError(existingData?.error || 'Could not load your medical history.');
				}
			}
			} catch {
					if (isMounted) {
							setError('An error occurred while loading your medical history.');
					}
		} finally {
				if (isMounted) { setIsLoading(false); }
		}

    return () => { isMounted = false; };
  }, [isWorkflowStep, initialData, userid]);


  // --- Input Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    onDirty?.();
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handlePersonalHistoryChange = (e) => {
    onDirty?.();
    handleHistoryChange(e, 'personalHistory', formData, setFormData);
  };
  const handleFamilyHistoryChange = (e) => {
    onDirty?.();
    handleHistoryChange(e, 'familyHistory', formData, setFormData);
  };

  // --- Handle form submission ---
  const handleSubmit = async (e) => {
      e.preventDefault();
      const effectiveSetError = isWorkflowStep ? parentSetError : setError;
      const effectiveSetLoading = isWorkflowStep ? parentSetLoading : setIsLoading;
      effectiveSetError(''); effectiveSetLoading(true); setIsSubmitting(true);
      if (!userid) {
          effectiveSetError('User information is missing.');
          effectiveSetLoading(false); setIsSubmitting(false);
          return;
      }

      // --- Prepare and Split Data ---
      const formatHistory = (historyObj, otherText) => {
          const selected = Object.entries(historyObj || {})
                               .filter(([key, value]) => key !== 'Other Illnesses' && value === true)
                               .map(([key]) => key);
          if (historyObj?.['Other Illnesses'] && otherText) {
              selected.push(`Other: ${otherText}`);
          } else if (historyObj?.['Other Illnesses']) {
             selected.push('Other Illnesses');
          }
          return selected; // Return array
      };

      // Data specifically for the health_profiles table
      const healthProfileData = {
          allergies: formData.allergies || 'None',
          medications: formData.medications || 'None',
          hospitalizations: formData.hospitalizations || 'None',
          personal_illness: formatHistory(formData.personalHistory, formData.personalHistoryOtherText), // Use correct API key
          family_illness: formatHistory(formData.familyHistory, formData.familyHistoryOtherText), // Use correct API key
          user_id: userid 
      };

      // --- End Prepare and Split Data ---

      try {
        // --- API Call Option C: Two separate calls ---
        let profileResult = { success: true }; // Assume success if no health data changed

        // Call updateHealthInfo only if there's data for it AND user update was successful (or skipped)
        const healthDataExists = Object.keys(healthProfileData).some(key => key !== 'userid'); // Exclude userid if added

        if (healthDataExists) {
             profileResult = await userContext.updateHealthProfile (healthProfileData); // Use the correct endpoint
        }

        // --- Process Combined Result ---
        const overallSuccess = profileResult.success;
        const combinedError = profileResult.error || null;

        if (overallSuccess) {
          if (isWorkflowStep) {
            // Pass back the raw form data structure for the parent workflow state
            onSuccess({
                registration_step: "medhistory",
                ...formData,
            });
          } else {
				toast.success("Medical history update successful");
            onSaveSuccess?.();
//            if (onSaveComplete) { onSaveComplete(); }
          }
        } else {
            effectiveSetError(combinedError || 'Failed to save medical history.');
        }
      } catch {
          effectiveSetError('An unexpected error occurred while saving medical history.');
      } finally {
          effectiveSetLoading(false); setIsSubmitting(false);
      }
  };

  // --- Render Logic ---
  const inputClassName = "mx-0 w-full p-1 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary-light text-sm";
  const checkboxClassName = "h-4 w-4 text-primary border-neutral-dark rounded focus:ring-primary";
  const labelClassName = "block text-sm font-medium text-gray-600 mb-1";

  if (isLoading) {
      return (
          <div className="flex justify-center items-center h-64">
              <Spinner />
          </div>
      );
  }
  // Show internal error if standalone and fetch failed (check a required field)
  if (!isWorkflowStep && error && !formData.allergies) {
     return <p className="text-center text-red-500 mt-4">{error}</p>;
  }

  return (
    // Outer container: Takes full height from parent
    <div className="h-full">
      {/* Form: flex column, takes full height, adds space between content and button */}
      <form onSubmit={handleSubmit} className="flex flex-col h-full space-y-4">
        {/* Content Wrapper: Scrolls if needed, ADD flex-grow */}
        <div className="flex-grow space-y-6 pb-6">
            <div>
              <h3 className="mb-1 font-semibold text-primary">Your Medical History</h3>
              <p className="text-sm text-neutral-dark">
                  Please provide details about your medical background. List "None" where applicable.
              </p>
            </div>
            {/* Flex Container for Layout */}
            <div className="flex flex-col lg:flex-row lg:space-x-6">
                {/* Section 1: General Info (1/2 width) */}
                <div className="w-full md:w-1/2 space-y-4 mb-6 lg:mb-0">
                    <h4 className="text-md font-semibold text-primary border-b pb-1">General Information</h4>
                    <div className="flex flex-col flex-shrink">
                        <label htmlFor="allergies" className={labelClassName+" ml-1"}>Allergies*</label>
                        <textarea id="allergies" name="allergies" placeholder='e.g., Penicillin, Peanuts, None' value={formData.allergies} onChange={handleChange} 
													className={`${inputClassName} max-w-full resize-y`} rows="3" required disabled={isSubmitting} />
                    </div>
                    <div className="flex flex-col flex-shrink">
                        <label htmlFor="medications" className={labelClassName+" ml-1"}>Ongoing Prescription Medications*</label>
                        <textarea id="medications" name="medications" placeholder='Include dosage (e.g., Lisinopril 10mg, None)' value={formData.medications} onChange={handleChange} className={inputClassName} rows="4" required disabled={isSubmitting} />
                    </div>
                    <div className="flex flex-col flex-shrink">
                        <label htmlFor="hospitalizations" className={labelClassName +" ml-1"}>Hospitalizations/Surgeries (Past 5 Years)*</label>
                        <textarea id="hospitalizations" name="hospitalizations" placeholder='Reason & Approx. Date (e.g., Appendectomy 2010, None)' value={formData.hospitalizations} onChange={handleChange} className={inputClassName} rows="4" required disabled={isSubmitting} />
                    </div>
                </div>

                {/* Section 2 & 3 Container (1/2 width) */}
                <div className="w-full lg:w-1/2 flex flex-col md:flex-row md:space-x-4">
                    {/* Section 2: Personal History (1/2 of remaining space) */}
	                    <fieldset className="w-full md:w-1/2 space-y-1 mb-6 md:mb-0">
	                         <legend className="text-md font-semibold text-primary border-b pb-1 w-full">Personal Illness History</legend>
	                        <p className="text-xs text-gray-500 italic pb-1">Check all that apply to you.</p>
	                        {Object.entries(conditionCategories).map(([category, conditions]) => (
	                            <fieldset key={`personal-cat-${category}`} className="mb-2">
	                            <legend className="text-sm font-medium text-gray-700">{category}</legend>
	                            {conditions.map(condition => {
                                const inputId = slugId('personal', condition);
                                return (
	                                <div key={`personal-${condition}`} className="flex items-center ml-2">
                                <input
                                    type="checkbox" id={inputId} name={condition}
                                    checked={!!formData.personalHistory?.[condition]}
                                    onChange={handlePersonalHistoryChange}
                                    className={checkboxClassName} disabled={isSubmitting}
                                />
                                <label htmlFor={inputId} className="ml-2 block text-sm text-neutral-dark">{condition}</label>
	                                </div>
	                            )})}
	                            </fieldset>
	                        ))}
                        {formData.personalHistory?.['Other Illnesses'] && (
                            <div className="pl-4 mt-1">
                                <label htmlFor="personalHistoryOtherText" className="block text-xs font-medium text-gray-500 mb-1">Please specify "Other":</label>
                                <input
                                    type="text" id="personalHistoryOtherText" name="personalHistoryOtherText"
                                    value={formData.personalHistoryOtherText} onChange={handleChange}
                                    className={inputClassName} disabled={isSubmitting}
                                />
                            </div>
                        )}
	                    </fieldset>

	                    {/* Section 3: Family History (1/2 of remaining space) */}
	                    <fieldset className="w-full md:w-1/2 space-y-1">
	                        <legend className="text-md font-semibold text-primary border-b pb-1 w-full">Family Illness History</legend>
	                        <p className="text-xs text-gray-500 italic pb-1">Immediate family only.</p>
	                        {Object.entries(conditionCategories).map(([category, conditions]) => (
	                            <fieldset key={`family-cat-${category}`} className="mb-2">
	                            <legend className="text-sm font-medium text-gray-700">{category}</legend>
	                            {conditions.map(condition => {
                                const inputId = slugId('family', condition);
                                return (
                                <div key={`family-${condition}`} className="flex items-center ml-2">
                                <input
                                    type="checkbox" id={inputId} name={condition}
                                    checked={!!formData.familyHistory?.[condition]}
                                    onChange={handleFamilyHistoryChange}
                                    className={checkboxClassName} disabled={isSubmitting}
                                />
                                <label htmlFor={inputId} className="ml-2 block text-sm text-neutral-dark">{condition}</label>
	                                </div>
	                            )})}
	                            </fieldset>
	                        ))}
	                        {formData.familyHistory?.['Other Illnesses'] && (
	                            <div className="pl-4 mt-1">
	                                <label htmlFor="familyHistoryOtherText" className="block text-xs font-medium text-gray-500 mb-1">Please specify "Other":</label>
	                                <input
	                                    type="text" id="familyHistoryOtherText" name="familyHistoryOtherText"
	                                    value={formData.familyHistoryOtherText} onChange={handleChange}
	                                    className={inputClassName} disabled={isSubmitting}
	                                />
	                            </div>
	                        )}
	                    </fieldset>
                </div> {/* End Sections 2 & 3 Container */}
            </div> {/* End Main Flex Container */}
        </div> {/* End scrollable content wrapper */}

        {/* --- Submission Area --- */}
        <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 pb-8">
            {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
            { !isWorkflowStep && (<button
                type="button"
                onClick={onSaveComplete}
                className={`w-full sm:w-1/4 bg-neutral-light text-neutral-dark p-3 rounded hover:bg-neutral-dark hover:text-white transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isSubmitting}
            >
                {isSubmitting ? '' : 'Close'}
            </button>
            )}
            <button
                type="submit"
                className={`${isWorkflowStep ? 'w-full':'w-full sm:w-1/4'} bg-button text-white p-3 rounded hover:bg-button-light transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Saving...' : (isWorkflowStep ? 'Save & Continue' : 'Save History Info')}
            </button>
        </div>


      </form>
    </div>
  );
}

export default MedHistory;
